import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addHashKey,
  batchDeleteHashKeys,
  getHashKeys,
  loginWithHashKey,
  removeApiKeyFromHashKey,
  addApiKeyToHashKey,
  renameHashKey,
} from "./hash.api";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";
import type {
  ILoginWithHashKeyPayload,
  ILoginWithHashKeyResponse,
  IAddHashKeyManagerPayload,
  IAddHashKeyManagerResponse,
  IBatchDeleteHashKeysPayload,
  IBatchDeleteHashKeysResponse,
  IRemoveApiKeyFromHashKeyPayload,
  IRemoveApiKeyFromHashKeyResponse,
  IAddApiKeyToHashKeyPayload,
  IAddApiKeyToHashKeyResponse,
  IRenameHashKeyResponse,
  IRenameHashKeyPayload,
} from "../types/hash.types";
import type {
  HashKeysResponse,
  HashKeyApiResponse,
  ApiKey,
} from "@/features/auth/types/hashKeys.types";
import { useAuthStore } from "../store/useAuthStore";
import { useNavigate } from "@tanstack/react-router";

export const useAddHashKey = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IAddHashKeyManagerResponse,
    Error,
    IAddHashKeyManagerPayload
  >({
    mutationFn: addHashKey,
    onSuccess: () => {
      toast.success("Hash key successfully created!");
      queryClient.invalidateQueries({ queryKey: ["hash-keys"] });
    },
    onError: (err: Error) => {
      toast.error("Failed to create hash key", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useLoginWithHashKey = () => {
  const navigate = useNavigate();
  const loginStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation<
    ILoginWithHashKeyResponse,
    Error,
    ILoginWithHashKeyPayload
  >({
    mutationFn: loginWithHashKey,
    onSuccess: async (data: ILoginWithHashKeyResponse) => {
      // Clear any cached data from previous sessions (defense in depth)
      queryClient.clear();

      const { user, token } = data;

      // Store the authentication data in the auth store
      loginStore.login({
        user: {
          id: user?.id || "",
          mongo_id: user?.mongo_id || "",
          email: user?.email || "",
          username: user?.username || null,
          profile_picture: user?.picture || "",
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          role_name: user?.role_name || undefined,
        },
        token,
        refresh_token: token, // Hash key login uses same token for both
      });

      // For hash key users, ONLY check role_name, ignore manager object
      // Hash key users should not have management access regardless of manager object presence
      const isAdmin = user?.role_name?.includes("admin") || false;
      const isManager = user?.role_name?.includes("manager") || false;

      loginStore.setAdminStatus(isAdmin);
      loginStore.setManagerStatus(isManager);

      toast.success("Login successful");

      // Role-based navigation - hash key users go to regular dashboard
      if (isAdmin) {
        navigate({ to: "/admin/dashboard" });
      } else if (isManager) {
        navigate({ to: "/manager/team-management" });
      } else {
        navigate({ to: "/", search: { section: undefined } });
      }
    },
    onError: (err: Error) => {
      toast.error("Failed to login", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export const useGetHashKeys = () => {
  return useQuery({
    queryKey: ["hash-keys"],
    queryFn: getHashKeys,
  });
};

export const useBatchDeleteHashKeys = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IBatchDeleteHashKeysResponse,
    Error,
    IBatchDeleteHashKeysPayload
  >({
    mutationFn: batchDeleteHashKeys,
    onSuccess: async () => {
      toast.success("Hash keys deactivated successfully");
      await queryClient.invalidateQueries({ queryKey: ["hash-keys"] });
    },
    onError: (err: Error) => {
      toast.error("Failed to deactivate hash keys", {
        description: extractErrorMessage(err),
      });
    },
  });
};

/**
 * Hook to remove an API key from a hash key
 * @param optimistic - Enable optimistic updates (default: true)
 */
export const useRemoveApiKeyFromHashKey = (optimistic: boolean = true) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<
    IRemoveApiKeyFromHashKeyResponse,
    Error,
    IRemoveApiKeyFromHashKeyPayload,
    { previousData: HashKeysResponse | undefined }
  >({
    mutationFn: removeApiKeyFromHashKey,

    onMutate: optimistic
      ? async (variables) => {
          await queryClient.cancelQueries({ queryKey: ["hash-keys"] });

          const previousData = queryClient.getQueryData<HashKeysResponse>([
            "hash-keys",
          ]);

          queryClient.setQueryData<HashKeysResponse>(
            ["hash-keys"],
            (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                hash_keys: oldData.hash_keys.map(
                  (hashKey: HashKeyApiResponse) =>
                    hashKey.hash_key_id === variables.hash_key_id
                      ? {
                          ...hashKey,
                          api_keys: hashKey.api_keys.filter(
                            (apiKey: ApiKey) =>
                              apiKey.api_key_id !== variables.api_key_id,
                          ),
                        }
                      : hashKey,
                ),
              };
            },
          );

          return { previousData };
        }
      : undefined,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["hash-keys"],
        refetchType: "inactive",
      });
      navigate({ to: "/manager/team-management" });
    },

    onError: (err, _variables, context) => {
      // Only show toast if not using custom error handling
      if (optimistic) {
        toast.error("Failed to remove API key from hash key", {
          description: extractErrorMessage(err),
        });
        if (context?.previousData) {
          queryClient.setQueryData(["hash-keys"], context.previousData);
        }
      }
    },
  });
};

export const useAddApiKeyToHashKey = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<
    IAddApiKeyToHashKeyResponse,
    Error,
    IAddApiKeyToHashKeyPayload,
    { previousData: HashKeysResponse | undefined }
  >({
    mutationFn: addApiKeyToHashKey,

    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["hash-keys"] });
      const previousData = queryClient.getQueryData<HashKeysResponse>([
        "hash-keys",
      ]);

      queryClient.setQueryData<HashKeysResponse>(["hash-keys"], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          hash_keys: oldData.hash_keys.map((hashKey: HashKeyApiResponse) =>
            hashKey.hash_key_id === variables.hash_key_id
              ? {
                  ...hashKey,
                  api_keys: [
                    ...hashKey.api_keys,
                    {
                      api_key_id: variables.api_key_id,
                      api_key_preview: "",
                      quota_remaining: 0,
                    },
                  ],
                }
              : hashKey,
          ),
        };
      });
      return { previousData };
    },

    onSuccess: () => {
      toast.success("API key added to hash key successfully");
      navigate({ to: "/manager/team-management" });
      queryClient.invalidateQueries({
        queryKey: ["hash-keys"],
      });
    },

    onError: (err, _variables, context) => {
      toast.error("Failed to add API key to hash key", {
        description: extractErrorMessage(err),
      });
      if (context?.previousData) {
        queryClient.setQueryData(["hash-keys"], context.previousData);
      }
    },
  });
};

export const useRenameHashKey = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<IRenameHashKeyResponse, Error, IRenameHashKeyPayload>({
    mutationFn: renameHashKey,
    onSuccess: () => {
      toast.success("Hash key renamed successfully");
      queryClient.invalidateQueries({ queryKey: ["hash-keys"] });
      navigate({ to: "/manager/team-management" });
    },
    onError: (err: Error) => {
      toast.error("Failed to rename hash key", {
        description: extractErrorMessage(err),
      });
    },
  });
};
